import React from 'react';
import tw, { css, styled } from 'twin.macro';
import { useAIUsage } from './useAIUsage';
import { useTranslation } from 'react-i18next';
import { format } from 'utils/time/format';
import { textColors } from 'styles/themeConstants';
import { SparklesIcon } from '@heroicons/react/24/outline';
import TwoColumnsCardSidebar from 'components/TwoColumnsCardSidebar';
import { PackPurchaseCard } from './PackPurchaseCard';
import { useFeatureFlagEnabled } from 'utils/useFeatureFlagEnabled';
import { useLocation } from 'react-router-dom';

const ProgressContainer = styled.div`
  ${tw`mt-2 mb-4`}
`;

const ProgressBar = styled.div`
  ${tw`h-2 rounded-full bg-neutral-supershy overflow-hidden`}
`;

const ProgressFill = styled.div`
  ${tw`h-full rounded-full transition-all duration-300`}
`;

function UsageCount({
  usageCount,
  usageLimit,
  isOverLimit,
  isNearLimit,
  className,
}: {
  usageCount: number;
  usageLimit: number;
  isOverLimit: boolean;
  isNearLimit: boolean;
  className?: string;
}) {
  return (
    <span
      className={className}
      css={[
        tw`text-sm font-semibold`,
        isOverLimit && !isNearLimit && tw`text-danger-moderate`,
        isOverLimit && isNearLimit && tw`text-warning-moderate`,
      ]}
    >
      {usageLimit - usageCount} / {usageLimit}
    </span>
  );
}

export function AIUsagePage() {
  const { data, isLoading } = useAIUsage();
  const { t } = useTranslation(['userSettings', 'common']);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const showPurchaseFromQuery = queryParams.get('showPurchase') === 'true';
  const displayPurchaseCredits =
    useFeatureFlagEnabled('display-purchase-credits') || showPurchaseFromQuery;

  if (isLoading) {
    return (
      <>
        <div tw="animate-pulse">
          <div tw="h-4 bg-neutral-quiet rounded w-1/4 mb-4"></div>
          <div tw="h-2 bg-neutral-quiet rounded w-full mb-4"></div>
          <div tw="h-4 bg-neutral-quiet rounded w-3/4"></div>
        </div>
      </>
    );
  }

  return (
    <TwoColumnsCardSidebar.Container>
      <TwoColumnsCardSidebar.Section
        title={
          <div tw="flex items-center gap-2">
            <SparklesIcon tw="shrink-0 h-6 w-6" />
            {t('userSettings:aiUsage.title')}
          </div>
        }
      >
        {data?.items.map(item => {
          const percentage =
            ((item.usageLimit - item.usageCount) / item.usageLimit) * 100;
          const isNearLimit = percentage <= 20 && percentage > 0;
          const isOverLimit = percentage <= 0;

          const expirationDate = item.expirationDate
            ? format(
                item.expirationDate,
                t('common:locale.short'),
                t('common:datePattern.dd/MM/yy'),
              )
            : undefined;
          return (
            <div
              data-testid={`usage-${item.creditType}`}
              key={item.creditType}
              tw="mb-6"
            >
              <div tw="flex flex-col">
                <div tw="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                  <div tw="flex items-center flex-wrap">
                    <span>
                      {item.creditType === 'pack' && !item.stripePricingId
                        ? t('userSettings:aiUsage.offeredCredits')
                        : t(
                            `userSettings:aiUsage.creditType.${item.creditType}`,
                          )}
                      &nbsp;
                    </span>
                    <span tw="text-neutral-quiet">
                      (
                      {expirationDate
                        ? t('userSettings:aiUsage.renewalDate', {
                            expirationDate,
                          })
                        : t('userSettings:aiUsage.noRenewalDate')}
                      )
                    </span>
                  </div>
                  <UsageCount
                    usageCount={item.usageCount}
                    usageLimit={item.usageLimit}
                    isOverLimit={isOverLimit}
                    isNearLimit={isNearLimit}
                    tw="hidden sm:block"
                  />
                </div>
                <ProgressContainer>
                  <ProgressBar>
                    <ProgressFill
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                      css={[
                        isOverLimit && tw`bg-danger-intense`,
                        isNearLimit &&
                          !isOverLimit &&
                          css`
                            background-color: ${textColors['warning-moderate']};
                          `,
                        !isNearLimit && !isOverLimit && tw`bg-brand-intense`,
                      ]}
                    />
                  </ProgressBar>
                </ProgressContainer>
                <div tw="flex justify-end -mt-2 sm:hidden">
                  <UsageCount
                    usageCount={item.usageCount}
                    usageLimit={item.usageLimit}
                    isOverLimit={isOverLimit}
                    isNearLimit={isNearLimit}
                  />
                </div>
              </div>
            </div>
          );
        })}

        {displayPurchaseCredits && (
          <div tw="mt-6">
            <PackPurchaseCard />
          </div>
        )}
      </TwoColumnsCardSidebar.Section>
    </TwoColumnsCardSidebar.Container>
  );
}
